// ============================================================================
// ReportHeader — the compact parameter + key-figure band at the top of a report
// ============================================================================
// Ported from Fairbooks (fairbooks-web/src/components/reports/ReportHeader.tsx)
// so CRS reports share its structure: shell → this one-line band → the grid,
// bounded and scrolling → a slim totals band under it. Big KPI cards are not
// used: on a report the rows are the content, and the figures that summarise
// them belong in one quiet line, not a wall of tiles above the list.
//
// Unlike Fairbooks (entity + financial year from the session), CRS reports are
// scoped by property and date, so every tile is supplied by the caller. Tiles
// marked `primary` (the report's scope — property, date) lead and are accented.
// ============================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

type IconKey = keyof typeof IconSystem;

export interface ReportHeaderItem {
  label: string;
  /** Shown as-is; '—' when empty. */
  value: React.ReactNode;
  /** IconSystem key; inferred from the label when omitted. */
  icon?: IconKey;
  /** The report's scope (property, date, period) — accented. */
  primary?: boolean;
  /** Tooltip with the figure's definition. */
  title?: string;
}

const inferIcon = (label: string): IconKey => {
  const key = label.toLowerCase();
  if (key.includes('property')) return 'home';
  if (key.includes('period') || key.includes('date') || key.includes('from')) return 'calendar';
  if (key.includes('pax') || key.includes('guest') || key.includes('male')) return 'users';
  if (key.includes('arr') || key.includes('rev') || key.includes('amount') || key.includes('net')
    || key.includes('receipt') || key.includes('refund')) return 'creditCard';
  if (key.includes('room') || key.includes('occup') || key.includes('vacant')) return 'grid';
  return 'tag';
};

const Tile: React.FC<{ item: ReportHeaderItem }> = ({ item }) => {
  const Icon = (IconSystem[item.icon ?? inferIcon(item.label)] ?? IconSystem.tag) as React.ComponentType<{ size?: number }>;
  const empty = item.value === null || item.value === undefined || item.value === '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0 0.65rem', minWidth: 0, flex: '0 0 auto' }}
      title={item.title}>
      <span aria-hidden style={{
        display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        color: item.primary ? 'var(--primary-color, var(--primary, #2563eb))' : 'var(--text-muted)',
      }}>
        <Icon size={12} />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{
          fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
          color: 'var(--text-muted)', fontWeight: 600, lineHeight: 1,
        }}>{item.label}</span>
        <span style={{
          fontSize: '0.75rem', fontWeight: item.primary ? 700 : 600, color: 'var(--text-primary)',
          lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '18rem',
        }}>{empty ? '—' : item.value}</span>
      </div>
    </div>
  );
};

const Divider = () => (
  <div aria-hidden style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-color)', margin: '0.25rem 0' }} />
);

const ReportHeader: React.FC<{ items: ReportHeaderItem[] }> = ({ items }) => (
  <div style={{
    position: 'relative', display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', rowGap: '0.35rem',
    padding: '0.4rem 0.3rem 0.4rem 0.65rem', background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)', borderRadius: '0.5rem 0.5rem 0 0', borderBottom: 'none',
    overflow: 'hidden', flexShrink: 0,
  }}>
    <span aria-hidden style={{
      position: 'absolute', top: 0, left: 0, bottom: 0, width: 3,
      background: 'var(--primary-color, var(--primary, #2563eb))', borderRadius: '0.5rem 0 0 0',
    }} />
    {items.map((t, i) => (
      <React.Fragment key={`${t.label}-${i}`}>
        {i > 0 && <Divider />}
        <Tile item={t} />
      </React.Fragment>
    ))}
  </div>
);

/**
 * The bordered body under a ReportHeader: a flex column whose grid gets a bounded
 * height and scrolls (a block parent with overflow hidden clips the rows and the
 * pager), with an optional slim band under the grid for totals and breakdowns.
 */
export const ReportBody: React.FC<{ children: React.ReactNode; footer?: React.ReactNode }> = ({ children, footer }) => (
  <div style={{
    flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
    border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
  }}>
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {children}
    </div>
    {footer && (
      <div style={{
        padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)',
        fontSize: '0.78rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem 1.25rem', alignItems: 'center', flexShrink: 0,
      }}>
        {footer}
      </div>
    )}
  </div>
);

export default ReportHeader;
